/**
 * Production-Grade Runtime Integration Injector
 * 
 * Safely injects scripts, meta tags, link tags, and JSON-LD schemas
 * into the DOM without blocking rendering or causing duplicates.
 * 
 * STRICT RULES:
 * - No dangerouslySetInnerHTML
 * - No eval/Function
 * - No runtime errors
 * - No duplicate injection
 * - Silent failure on errors
 * - Works with React SPA route changes
 */

// In-memory registry to track injected integrations
const injectedRegistry = new Set();
const scriptRegistry = new Map(); // id -> element
const metaLinkRegistry = new Map(); // uniqueKey -> element

/**
 * Get current environment
 * @returns {string} Current environment
 */
const getCurrentEnv = () => {
  if (typeof window === 'undefined') return 'development';
  // Check if we're in development mode
  if (process.env.NODE_ENV === 'development') return 'development';
  // Default to production for deployed apps
  return 'production';
};

/**
 * Check if consent is granted for a specific category
 * @param {string|boolean} consentCategory - Consent category ("analytics" | "marketing") or boolean (legacy)
 * @param {Object} consentState - Consent state object from ConsentContext
 * @returns {boolean}
 */
const hasConsent = (consentCategory, consentState) => {
  // Legacy support: if consentCategory is boolean, return it
  if (typeof consentCategory === 'boolean') {
    return consentCategory === true;
  }
  
  // If consentState is not provided, default to false (conservative)
  if (!consentState || typeof consentState !== 'object') {
    return false;
  }
  
  // Handle consent categories
  if (consentCategory === 'necessary') {
    return true; // Necessary scripts always allowed
  }
  
  if (consentCategory === 'analytics') {
    return consentState.analytics === true;
  }
  
  if (consentCategory === 'marketing') {
    return consentState.marketing === true;
  }
  
  // Unknown category, default to false
  return false;
};

/**
 * Generate a unique key for meta/link tags for deduplication
 * @param {Object} tag - Meta or link tag element
 * @returns {string} Unique key
 */
const getMetaLinkKey = (tag) => {
  if (tag.getAttribute('name')) {
    return `meta:name:${tag.getAttribute('name')}`;
  }
  if (tag.getAttribute('property')) {
    return `meta:property:${tag.getAttribute('property')}`;
  }
  if (tag.getAttribute('rel') && tag.getAttribute('href')) {
    return `link:rel:${tag.getAttribute('rel')}:href:${tag.getAttribute('href')}`;
  }
  return null;
};

/**
 * Check if a meta/link tag already exists
 * @param {string} uniqueKey - Unique key for the tag
 * @param {string} type - Type of tag (meta or link)
 * @returns {boolean} True if duplicate exists
 */
const checkMetaLinkDuplicate = (uniqueKey, type) => {
  if (!uniqueKey) return false;
  
  // Check in-memory registry
  if (metaLinkRegistry.has(uniqueKey)) {
    return true;
  }
  
  // Check DOM for existing tags
  const selector = type === 'meta' ? 'meta' : 'link';
  const tags = document.querySelectorAll(selector);
  
  for (const tag of tags) {
    const key = getMetaLinkKey(tag);
    if (key === uniqueKey) {
      metaLinkRegistry.set(uniqueKey, tag);
      return true;
    }
  }
  
  return false;
};

/**
 * Check if a script already exists
 * @param {string} id - Script ID
 * @param {string} src - Script src (if external)
 * @returns {boolean} True if duplicate exists
 */
const checkScriptDuplicate = (id, src) => {
  // Check in-memory registry
  if (id && injectedRegistry.has(`script:${id}`)) {
    return true;
  }
  
  // Check window marker (for scripts that set global flags)
  if (id && typeof window !== 'undefined' && window[`__integrationLoaded_${id}`]) {
    return true;
  }
  
  // Check DOM for existing scripts
  const scripts = document.querySelectorAll('script');
  for (const script of scripts) {
    if (id && script.id === id) {
      return true;
    }
    if (src && script.src === src) {
      return true;
    }
  }
  
  return false;
};

/**
 * Check if JSON-LD already exists
 * @param {string} id - JSON-LD ID
 * @returns {boolean} True if duplicate exists
 */
const checkJsonLdDuplicate = (id) => {
  if (!id) return false;
  
  // Check in-memory registry
  if (injectedRegistry.has(`jsonld:${id}`)) {
    return true;
  }
  
  // Check DOM
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  for (const script of scripts) {
    if (script.id === id) {
      return true;
    }
  }
  
  return false;
};

/**
 * Parse meta tag attributes from content string
 * Expected format: name="value" or property="value" content="value"
 * @param {string} content - Content string with attributes
 * @returns {Object} Parsed attributes
 */
const parseMetaAttributes = (content) => {
  const attrs = {};
  const regex = /(\w+)="([^"]*)"/g;
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    attrs[match[1]] = match[2];
  }
  
  return attrs;
};

/**
 * Parse link tag attributes from content string
 * @param {string} content - Content string with attributes
 * @returns {Object} Parsed attributes
 */
const parseLinkAttributes = (content) => {
  return parseMetaAttributes(content); // Same parsing logic
};

/**
 * Parse script attributes from content string or extract src
 * @param {string} content - Script content (can be src URL or HTML)
 * @returns {Object} Parsed attributes
 */
const parseScriptContent = (content) => {
  // If content is a URL (starts with http:// or https://), treat as src
  if (content.trim().match(/^https?:\/\//)) {
    return { src: content.trim() };
  }
  
  // Otherwise, parse as HTML attributes
  return parseMetaAttributes(content);
};

/**
 * Inject a meta tag
 * @param {Object} integration - Integration item
 * @returns {boolean} Success status
 */
const injectMeta = (integration) => {
  try {
    const { id, uniqueKey, content } = integration;
    
    if (!uniqueKey) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[IntegrationInjector] Meta tag missing uniqueKey:', id);
      }
      return false;
    }
    
    // Check for duplicates
    if (checkMetaLinkDuplicate(uniqueKey, 'meta')) {
      return false; // Already exists, skip silently
    }
    
    // Parse attributes
    const attrs = parseMetaAttributes(content);
    
    // Create meta element
    const meta = document.createElement('meta');
    
    // Set attributes safely
    Object.keys(attrs).forEach(key => {
      if (key !== 'tag' && key !== 'type') {
        meta.setAttribute(key, attrs[key]);
      }
    });
    
    // Append to head
    document.head.appendChild(meta);
    
    // Register
    metaLinkRegistry.set(uniqueKey, meta);
    if (id) {
      injectedRegistry.add(`meta:${id}`);
    }
    
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[IntegrationInjector] Error injecting meta:', error);
    }
    return false;
  }
};

/**
 * Inject a link tag
 * @param {Object} integration - Integration item
 * @returns {boolean} Success status
 */
const injectLink = (integration) => {
  try {
    const { id, uniqueKey, content } = integration;
    
    if (!uniqueKey) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[IntegrationInjector] Link tag missing uniqueKey:', id);
      }
      return false;
    }
    
    // Check for duplicates
    if (checkMetaLinkDuplicate(uniqueKey, 'link')) {
      return false; // Already exists, skip silently
    }
    
    // Parse attributes
    const attrs = parseLinkAttributes(content);
    
    // Create link element
    const link = document.createElement('link');
    
    // Set attributes safely
    Object.keys(attrs).forEach(key => {
      if (key !== 'tag' && key !== 'type') {
        link.setAttribute(key, attrs[key]);
      }
    });
    
    // Append to head
    document.head.appendChild(link);
    
    // Register
    metaLinkRegistry.set(uniqueKey, link);
    if (id) {
      injectedRegistry.add(`link:${id}`);
    }
    
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[IntegrationInjector] Error injecting link:', error);
    }
    return false;
  }
};

/**
 * Inject a script tag
 * @param {Object} integration - Integration item
 * @returns {boolean} Success status
 */
const injectScript = (integration) => {
  try {
    const { id, placement, content } = integration;
    
    // Parse script content
    const scriptAttrs = parseScriptContent(content);
    const src = scriptAttrs.src;
    const scriptText = scriptAttrs.textContent || (src ? null : content);
    
    // Check for duplicates
    if (checkScriptDuplicate(id, src)) {
      return false; // Already exists, skip silently
    }
    
    // Create script element
    const script = document.createElement('script');
    
    if (id) {
      script.id = id;
    }
    
    if (src) {
      script.src = src;
      script.async = true; // External scripts should be async
    } else if (scriptText) {
      script.textContent = scriptText;
    } else {
      // Invalid script content
      return false;
    }
    
    // Determine placement target
    let target = null;
    if (placement === 'head' || !placement) {
      target = document.head;
    } else if (placement === 'body_start') {
      target = document.body;
      if (target && target.firstChild) {
        target.insertBefore(script, target.firstChild);
        if (id) {
          injectedRegistry.add(`script:${id}`);
          scriptRegistry.set(id, script);
        }
        return true;
      }
      return false;
    } else if (placement === 'body_end') {
      target = document.body;
    } else {
      // Invalid placement, default to head
      target = document.head;
    }
    
    if (!target) {
      return false; // Target not available yet
    }
    
    // Append script
    if (placement === 'body_start') {
      // Already handled above
    } else {
      target.appendChild(script);
    }
    
    // Register
    if (id) {
      injectedRegistry.add(`script:${id}`);
      scriptRegistry.set(id, script);
      
      // Set global marker for scripts that check this
      if (typeof window !== 'undefined') {
        window[`__integrationLoaded_${id}`] = true;
      }
    }
    
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[IntegrationInjector] Error injecting script:', error);
    }
    return false;
  }
};

/**
 * Inject JSON-LD schema
 * @param {Object} integration - Integration item
 * @returns {boolean} Success status
 */
const injectJsonLd = (integration) => {
  try {
    const { id, content } = integration;
    
    // Check for duplicates
    if (id && checkJsonLdDuplicate(id)) {
      return false; // Already exists, skip silently
    }
    
    // Validate JSON
    let jsonData;
    try {
      jsonData = JSON.parse(content);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[IntegrationInjector] Invalid JSON-LD content:', id);
      }
      return false;
    }
    
    // Create script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    
    if (id) {
      script.id = id;
    }
    
    script.textContent = JSON.stringify(jsonData);
    
    // JSON-LD always goes in head
    document.head.appendChild(script);
    
    // Register
    if (id) {
      injectedRegistry.add(`jsonld:${id}`);
    }
    
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[IntegrationInjector] Error injecting JSON-LD:', error);
    }
    return false;
  }
};

/**
 * Main injection function
 * @param {Array} integrations - Array of integration items
 * @param {string} env - Current environment
 * @param {boolean} consentState - Consent state
 */
export const injectIntegrations = (integrations = [], env = null, consentState = true) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return; // Server-side, skip
  }
  
  const currentEnv = env || getCurrentEnv();
  
  // Filter integrations
  const filtered = integrations.filter(integration => {
    // Must be enabled
    if (!integration.enabled) return false;
    
    // Check environment
    const envList = integration.env || ['production'];
    if (!envList.includes(currentEnv)) return false;
    
    // Check consent
    if (integration.requiresConsent && !hasConsent(consentState)) {
      return false;
    }
    
    return true;
  });
  
  // Inject each integration
  filtered.forEach(integration => {
    try {
      const { type, id } = integration;
      
      // Skip if already injected (idempotency check)
      if (id && injectedRegistry.has(`${type}:${id}`)) {
        return;
      }
      
      switch (type) {
        case 'meta':
          injectMeta(integration);
          break;
        case 'link':
          injectLink(integration);
          break;
        case 'script':
          injectScript(integration);
          break;
        case 'jsonld':
          injectJsonLd(integration);
          break;
        default:
          if (process.env.NODE_ENV === 'development') {
            console.warn('[IntegrationInjector] Unknown integration type:', type);
          }
      }
    } catch (error) {
      // Silent failure - never crash the app
      if (process.env.NODE_ENV === 'development') {
        console.error('[IntegrationInjector] Error processing integration:', error);
      }
    }
  });
};

/**
 * Clear registry (useful for testing or cleanup)
 */
export const clearRegistry = () => {
  injectedRegistry.clear();
  scriptRegistry.clear();
  metaLinkRegistry.clear();
};

/**
 * React Hook for integration injection
 * Use this in React components
 */
export const useIntegrationInjector = (integrations, env, consentState) => {
  if (typeof window === 'undefined') {
    return;
  }
  
  // Use requestIdleCallback for non-blocking injection
  const scheduleInjection = () => {
    if (typeof window.requestIdleCallback !== 'undefined') {
      window.requestIdleCallback(() => {
        injectIntegrations(integrations, env, consentState);
      }, { timeout: 2000 });
    } else {
      // Fallback to setTimeout
      setTimeout(() => {
        injectIntegrations(integrations, env, consentState);
      }, 0);
    }
  };
  
  // Inject after initial render
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInjection);
  } else {
    scheduleInjection();
  }
};
